import React from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { InputForm } from '../../../../../components/inputs/forms/InputForm';
import {
  ESTADO_CIVIL_OPTIONS,
  ESTADOS_QUE_EXIGEM_REGIME,
  IWizardFormValues,
  REGIME_CASAMENTO_OPTIONS,
} from '../schema';
import { EnderecoForm } from './EnderecoForm';
import { OrgaoEmissorInput } from './OrgaoEmissorInput';
import { CNPJ_MASK } from '../../../../../utils/documentValidation';

/**
 * Transformação EI→LTDA (Fase 2) — passo do Empresário Individual de origem.
 *
 * Coleta a identidade do EI (denominação + CNPJ/NIRE/data de arquivamento, que a
 * LTDA herda por sub-rogação) + a qualificação do titular (PF) + DOIS endereços
 * (residência do empresário e sede ORIGINAL do EI). O CNPJ/NIRE/data vivem no
 * nível do contrato; o resto vai no sub-objeto `dados_ei` (JSONB).
 *
 * Não coleta capital/CNAEs/foro/sede nova da LTDA — isso é o passo "Empresa"
 * (`StepEmpresa`), pois a Parte B é um corpo de constituição.
 */
export const StepEmpresarioIndividual: React.FC = () => {
  const { control } = useFormContext<IWizardFormValues>();
  const estadoCivil = useWatch({ control, name: 'dados_ei.estado_civil' });
  const exigeRegime = ESTADOS_QUE_EXIGEM_REGIME.includes(estadoCivil as never);

  return (
    <Box>
      <Text fontWeight="bold" fontSize="lg" mb={1}>
        Empresário Individual de origem
      </Text>
      <Text fontSize="sm" color="gray.600" mb={4}>
        Dados do EI que será transformado em sociedade limitada. CNPJ, NIRE e data de arquivamento
        são herdados pela LTDA (sub-rogação).
      </Text>

      {/* Identidade do EI (herdada pela LTDA) */}
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mb={3}>
        <InputForm
          name="dados_ei.denominacao"
          label="Denominação do Empresário Individual"
          isRequired
          controlMb={0}
        />
        <InputForm name="cnpj" label="CNPJ" mask={CNPJ_MASK} isRequired controlMb={0} />
        <InputForm name="nire" label="NIRE" controlMb={0} helper="Registro na Junta" />
        <InputForm
          name="data_constituicao"
          label="Data de arquivamento do EI"
          type="date"
          controlMb={0}
        />
      </SimpleGrid>

      <Divider my={4} />

      <Text fontWeight="bold" mb={2}>
        Qualificação do empresário (titular)
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mb={3}>
        <InputForm name="dados_ei.nome" label="Nome completo" isRequired controlMb={0} />
        <InputForm name="dados_ei.cpf" label="CPF" mask="999.999.999-99" isRequired controlMb={0} />
        <InputForm name="dados_ei.rg" label="RG" isRequired controlMb={0} />
        <OrgaoEmissorInput
          name="dados_ei.rg_orgao"
          label="Órgão emissor do RG"
          isRequired
          controlMb={0}
        />
        <InputForm name="dados_ei.rg_via" label="Via do RG" controlMb={0} helper="Ex.: 2ª via" />
        <InputForm name="dados_ei.nacionalidade" label="Nacionalidade" isRequired controlMb={0} />
        <InputForm name="dados_ei.profissao" label="Profissão" isRequired controlMb={0} />
        <InputForm
          name="dados_ei.dt_nascimento"
          label="Data de nascimento"
          type="date"
          isRequired
          controlMb={0}
        />
        <InputForm
          name="dados_ei.naturalidade"
          label="Naturalidade"
          controlMb={0}
          helper="Ex.: Goiânia – GO"
        />
        <Controller
          control={control}
          name="dados_ei.genero"
          render={({ field, fieldState }) => (
            <FormControl isRequired isInvalid={!!fieldState.error} mb={0}>
              <FormLabel>Gênero</FormLabel>
              <Select placeholder="Selecione" {...field} value={field.value ?? ''}>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
              </Select>
              {fieldState.error?.message && (
                <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
              )}
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="dados_ei.estado_civil"
          render={({ field, fieldState }) => (
            <FormControl isRequired isInvalid={!!fieldState.error} mb={0}>
              <FormLabel>Estado civil</FormLabel>
              <Select placeholder="Selecione" {...field} value={field.value ?? ''}>
                {ESTADO_CIVIL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              {fieldState.error?.message && (
                <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
              )}
            </FormControl>
          )}
        />
        {exigeRegime && (
          <Controller
            control={control}
            name="dados_ei.regime_casamento"
            render={({ field, fieldState }) => (
              <FormControl isRequired isInvalid={!!fieldState.error} mb={0}>
                <FormLabel>Regime de bens</FormLabel>
                <Select placeholder="Selecione o regime" {...field} value={field.value ?? ''}>
                  {REGIME_CASAMENTO_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
                {fieldState.error?.message && (
                  <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                )}
              </FormControl>
            )}
          />
        )}
      </SimpleGrid>

      <Divider my={4} />

      <EnderecoForm
        basePath="dados_ei.endereco_estruturado"
        label="Endereço de residência do empresário"
        isRequired
      />

      <Box mt={4}>
        <EnderecoForm
          basePath="dados_ei.sede_estruturada"
          label="Sede original do Empresário Individual"
          isRequired
        />
      </Box>
    </Box>
  );
};
